import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { workspaceRoute } from "@/lib/constants";
import { useUIStore } from "@/stores/useUIStore";
import { subscribeCelebrations } from "./celebrationBus";
import { CELEBRATION_PRESETS } from "./celebrationRules";
import { fireConfetti } from "./confetti";
import { playChime } from "./chime";

/** Long enough for the confetti and headline to land before the page changes. */
export const NEXT_QUEST_REDIRECT_MS = 3500;

/** Typing in the composer or any field: moving the page would eat their words. */
function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Headless: renders nothing, plays whatever the bus emits. Mounted once in
 * main.tsx. Store values are read at emit time via getState() so the single
 * subscription never goes stale.
 */
export function CelebrationLayer(): null {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const unsubscribe = subscribeCelebrations((e) => {
      const ui = useUIStore.getState();
      // The actor already got their own success toast; the counterparty
      // needs a line saying WHY the confetti is falling.
      if (e.source === "notification" && e.kind !== "quest_published") {
        // The worker's flow ends here. Their next step is asked in that
        // quest's chat ("Keep finding quests like this?"), which is what
        // sends them on to Discover — so this goes to the chat, never
        // straight past the question. Already in it: nothing to move.
        const chat = e.workerJobEnded && e.workerQuestId ? workspaceRoute(e.workerQuestId) : null;
        const goToChat = chat !== null && window.location.pathname !== chat;
        ui.addToast({
          type: "success",
          message: t(`celebrations.headline.${e.kind}`),
          // Longer than the 4s default so there's time to tap it.
          ...(goToChat && {
            action: { label: t("celebrations.whatsNext"), to: chat },
            durationMs: 10_000,
          }),
        });
        // Not a question — closing the pop-up (✕) is the one tap that keeps
        // them here, and typing keeps them here too; the link stays either way.
        if (goToChat) {
          const { toasts } = useUIStore.getState();
          const toastId = toasts[toasts.length - 1]?.id;
          const timer = setTimeout(() => {
            timers.delete(timer);
            const stillShown = useUIStore.getState().toasts.some((x) => x.id === toastId);
            if (!stillShown || isTyping() || window.location.pathname === chat) return;
            useUIStore.getState().removeToast(toastId!);
            navigate(chat);
          }, NEXT_QUEST_REDIRECT_MS);
          timers.add(timer);
        }
      }
      if (ui.celebrationSound) playChime(CELEBRATION_PRESETS[e.kind].notes);
      if (!prefersReducedMotion()) void fireConfetti(e.kind);
    });
    // `navigate` changes with the route, so this also runs when the worker
    // moves on by themselves: a pending redirect never overrides their choice.
    return () => {
      unsubscribe();
      timers.forEach(clearTimeout);
    };
  }, [t, navigate]);

  return null;
}
